/**
 * Nội dung tiếng Việt — Prompt.md §7, §9, §10, §20.
 *
 * Tập trung mọi string vào một file: đủ để thêm i18n sau này mà chưa phải kéo i18n lib vào
 * MVP (§36 không over-engineering).
 */

import type { AlertMessageKey, CameraPlacement, PostureStatus } from "@studystraight/shared-types";

/** §20 — không cảnh báo theo hướng trách móc. */
export const alertMessages: Record<AlertMessageKey, string> = {
  HEAD_DOWN: "Con ngẩng đầu lên một chút nhé.",
  TOO_CLOSE: "Con đang ngồi hơi gần màn hình.",
  LEANING_FORWARD: "Con điều chỉnh người thẳng hơn một chút nhé.",
  SHOULDER_TILT: "Con ngồi cân hai vai lại nhé.",
  GENERIC: "Con điều chỉnh lại tư thế nhé.",
  EYE_BREAK: "Con nghỉ mắt 20 giây nhé, nhìn ra xa một chút.",
};

/** §9 */
export const brand = {
  name: "StudyStraight",
  tagline: "Học tập thoải mái. Duy trì tư thế tốt.",
  primaryCta: "Bắt đầu phiên học",
  history: "Lịch sử",
  settings: "Cài đặt",
} as const;

/** §7 — bắt buộc hiển thị. */
export const privacyNotice =
  "Hình ảnh camera được xử lý trực tiếp trên thiết bị và không được tải lên máy chủ.";

/** §10 + quyết định #20 — 2 card chọn chế độ đặt máy. */
export const placementGuide: Record<
  CameraPlacement,
  { label: string; hint: string; badge?: string }
> = {
  FRONTAL: {
    label: "Đặt trước mặt",
    hint: "Dựng điện thoại trước mặt con, camera thấy đầu và hai vai.",
    badge: "Phổ biến",
  },
  SIDE: {
    label: "Đặt bên cạnh",
    hint: "Đặt điện thoại bên cạnh bàn, camera nhìn ngang thấy đầu, vai và hông.",
  },
};

/** §10 */
export const setupMessages = {
  frameHint: "Đặt điện thoại sao cho camera nhìn thấy đầu, vai và phần thân trên.",
  adjustHint: "Hãy điều chỉnh vị trí điện thoại một chút.",
  missingHips: "Chế độ đặt bên cạnh cần thấy được hông của con.",
  placementMismatch: "Có vẻ con đang đặt máy khác chế độ đã chọn. Chuyển chế độ nhé?",
  ready: "Đã thấy rõ con. Bắt đầu hiệu chỉnh nhé.",
} as const;

/**
 * Trạng thái của bộ nhận diện tư thế (§10, §33).
 *
 * `failed` là một trạng thái **bắt buộc hiển thị**, không phải nice-to-have: nếu MediaPipe không
 * khởi tạo được (WASM/WebGL trên iOS Safari là ca hay gặp nhất), màn hình Setup sẽ đứng mãi ở
 * `loading` và người dùng thấy "camera lên nhưng app không nhận tư thế" mà không có gì để báo lỗi —
 * trên điện thoại thì cũng không có console để xem.
 */
export const visionMessages = {
  loading: "Đang tải bộ nhận diện tư thế…",
  failed: "Không tải được bộ nhận diện tư thế trên thiết bị này.",
  /**
   * `stage: "runtime"` — đã nạp XONG rồi mới chết giữa lúc nhận diện.
   *
   * Dùng chung câu `failed` cho ca này là nói sai sự thật, và cái giá không phải là câu chữ: người
   * kiểm thử trên iPhone đọc "không tải được" sẽ đi soi mạng với dung lượng file, trong khi lỗi
   * thật nằm ở lúc inference. Đúng ca đó đã xảy ra trên thiết bị thật (`Aborted()` từ wasm).
   */
  crashed: "Bộ nhận diện tư thế vừa dừng giữa lúc đang chạy.",
  /**
   * `stage: "webgl"` — kiểu hỏng duy nhất người dùng tự xử lý được, nên là câu duy nhất có hướng dẫn.
   *
   * Thứ tự việc làm xếp theo **bằng chứng**, không theo cảm giác. Đo được trên iPhone iOS 18.7.2
   * thật: cả 4 biến thể context (WebGL2 gắn DOM / rời DOM / `OffscreenCanvas` / WebGL1) đều trả
   * `getError 0x9242` = `CONTEXT_LOST_WEBGL` **ngay lúc vừa tạo**.
   *
   * Chi tiết quyết định thứ tự: context **được cấp rồi mất ngay** khác hẳn context **không được
   * cấp**. Tính năng bị tắt bằng policy thì `getContext` trả `null`; còn trả về object rồi báo lost
   * là dấu hiệu **GPU process từ chối cấp**. Trên iOS, WebKit dùng một GPU process dùng chung cho
   * cả máy, nên process đó hỏng thì mọi trình duyệt đều chết — và khởi động lại máy là cách duy
   * nhất dựng lại nó. Vì vậy nó đứng đầu danh sách.
   *
   * Hai giả thuyết đã bị đo và loại, đừng đưa lại vào: (1) "hết hạn mức context vì app/tab khác
   * chiếm" — `get.webgl.org`, trang không liên quan gì, cũng chết trong tab mới; (2) "cấu hình
   * Safari" — Chrome iOS (process + cấu hình riêng) cũng chết y hệt.
   *
   * Tên setting phải ghi **đúng chữ iOS hiển thị**: iOS 18 tiếng Việt gọi Lockdown Mode là
   * "Chế độ phong tỏa" trong "Quyền riêng tư & Bảo mật". Bản trước ghi "Chế độ khoá" / "Riêng tư &
   * Bảo mật" — người dùng tìm không thấy chữ đó trên máy rồi kết luận hướng dẫn sai, tức là hỏng
   * đúng thứ câu này tồn tại để làm.
   *
   * KHÔNG nhắc profile MDM ở đây nữa: tôi từng xếp nó vào danh sách, nhưng iOS **không có** khoá
   * restriction nào tắt được WebGL, nên đó là suy đoán chứ không phải nguyên nhân đã biết. Đẩy người
   * dùng đi hỏi IT vì một giả thuyết yếu là làm mất thời gian của hai người.
   */
  webglBlocked:
    "Trình duyệt đang không cho dùng đồ hoạ 3D (WebGL) nên không nhận diện được tư thế. " +
    "Thử lần lượt: khởi động lại máy (tắt nguồn hẳn, không phải chỉ khoá màn hình); " +
    "kiểm tra Chế độ phong tỏa ở cuối Cài đặt → Quyền riêng tư & Bảo mật; " +
    "cập nhật iOS ở Cài đặt → Cài đặt chung → Cập nhật phần mềm. " +
    "Nếu vẫn không được thì máy này không chạy được, hãy dùng máy khác.",
  retry: "Thử lại",
} as const;

/**
 * §32 — cập nhật PWA.
 *
 * Nội dung nói rõ "cập nhật xong rồi, chỉ cần mở lại" chứ không phải "đang tải bản mới": lúc thông
 * báo này hiện ra thì bản mới ĐÃ tải xong, và ngưỡng threshold sau khi tune ở Bước 17 chỉ có hiệu
 * lực sau lần mở lại đó — nên câu chữ không được làm người dùng nghĩ là phải chờ.
 */
export const pwaMessages = {
  updateReady: "Đã có bản cập nhật mới của StudyStraight.",
  reload: "Mở lại ngay",
  later: "Để sau",
  offlineReady: "Đã có thể dùng khi không có mạng.",
} as const;

/** §11 */
export const calibrationMessages = {
  intro: "Con ngồi ở tư thế học tập thoải mái nhé.",
  countdown: "Chuẩn bị...",
  collecting: "Con giữ nguyên tư thế trong vài giây.",
  done: "Đã ghi nhận tư thế của con.",
  NOT_ENOUGH_SAMPLES: "Chưa thu đủ dữ liệu. Con thử lại nhé.",
  TOO_UNSTABLE: "Con ngồi yên hơn một chút rồi thử lại nhé.",
  LANDMARKS_MISSING: "Camera chưa thấy rõ con. Điều chỉnh vị trí điện thoại rồi thử lại nhé.",
  PLACEMENT_MISMATCH: "Chế độ đặt máy không khớp với vị trí thực tế. Kiểm tra lại nhé.",
} as const;

/** §24 — nhãn ngắn hiển thị dưới điểm số. Không đưa thông tin kỹ thuật lên UI người dùng. */
export const statusLabels: Record<PostureStatus, string> = {
  GOOD: "TỐT",
  NEEDS_ADJUSTMENT: "CẦN ĐIỀU CHỈNH",
  BAD: "CHƯA TỐT",
};

export const monitoringMessages = {
  title: "Đang theo dõi",
  trackingLost: "Không thấy con trong khung hình.",
  paused: "Phiên học đang tạm dừng.",
  pause: "Tạm dừng",
  resume: "Tiếp tục",
  recalibrate: "Hiệu chỉnh lại",
  end: "Kết thúc phiên",
  alerts: "Nhắc",
  goal: "Còn lại",
  goalReached: "Con đã học đủ thời gian đặt ra rồi. Nghỉ một chút nhé.",
  eyeBreakDone: "Con đã nghỉ mắt xong",
  /** §20 — thiết bị không có giọng tiếng Việt: nhắc bằng chữ thay vì đọc bằng giọng lạ. */
  voiceMissing: "Thiết bị chưa có giọng đọc tiếng Việt — con sẽ được nhắc bằng chữ.",
  storageFailed: "Không lưu được dữ liệu phiên học trên thiết bị này.",
  ending: "Đang lưu phiên học…",
} as const;

/** §26 */
export const reportMessages = {
  title: "Phiên học hôm nay",
  goodPosture: "Tư thế tốt",
  averageScore: "Điểm trung bình",
  needsAdjustment: "Cần điều chỉnh",
  noVideo: "Không lưu video.",
  tooShort: "Phiên quá ngắn để thống kê. Phiên này không được lưu vào lịch sử.",
  duration: "Thời gian học",
  alerts: "Số lần nhắc",
  eyeBreaks: "Lần nghỉ mắt",
  issuesTitle: "Những điều cần để ý",
  times: "lần",
  backHome: "Về trang chính",
  /** Phân biệt "0 lần" với "không đo được" — xem StudySession.excludedIssues. */
  notMeasured: "Chế độ đặt máy này không đo được mục này.",
  issueLabels: {
    HEAD_DOWN: "Cúi đầu",
    TOO_CLOSE: "Ngồi quá gần",
    LEANING_FORWARD: "Nghiêng người",
    SHOULDER_TILT: "Lệch vai",
  },
} as const;

export const cameraErrorMessages = {
  PERMISSION_DENIED:
    "Ứng dụng chưa được cấp quyền camera. Hãy mở lại quyền camera trong cài đặt của trình duyệt.",
  NO_DEVICE: "Không tìm thấy camera trên thiết bị này.",
  IN_USE: "Camera đang được ứng dụng khác sử dụng. Hãy đóng ứng dụng đó rồi thử lại.",
  /**
   * Nhánh bị bỏ sót thường xuyên nhất: trên http://<ip-lan> từ điện thoại, getUserMedia
   * KHÔNG TỒN TẠI (không phải bị deny). Không phân biệt được là mất hàng giờ debug một
   * "permission bug" không hề tồn tại.
   */
  INSECURE_CONTEXT:
    "Trình duyệt chỉ cho dùng camera trên kết nối HTTPS. Hãy mở ứng dụng qua địa chỉ https:// (chạy `npm run dev:https`).",
  UNKNOWN: "Không mở được camera.",
} as const;

/** §27 */
export const historyMessages = {
  title: "Lịch sử phiên học",
  empty: "Chưa có phiên học nào được lưu.",
  loading: "Đang đọc dữ liệu…",
  deleteAll: "Xoá toàn bộ dữ liệu",
  deleted: "Đã xoá toàn bộ dữ liệu trên thiết bị này.",
  open: "Xem chi tiết",
} as const;

/** §23, §25, §27 — màn hình Settings. */
export const settingsMessages = {
  title: "Cài đặt",
  placement: "Chế độ đặt máy",
  placementHint: "Đổi chế độ sẽ cần hiệu chỉnh lại ở phiên học sau.",
  timer: "Thời gian học mục tiêu",
  custom: "Tự chọn",
  customUnit: "phút",
  voice: "Nhắc bằng giọng nói",
  eyeBreak: "Nhắc nghỉ mắt mỗi 25 phút",
  debug: "Chế độ debug",
  debugHint: "Hiện số liệu kỹ thuật và nút xuất log khi đang học.",
  developer: "Dành cho người phát triển",
  developerHint:
    "Ngưỡng phát hiện theo chế độ đặt máy. Để trống nghĩa là dùng giá trị mặc định của ứng dụng.",
  warningDelta: "Ngưỡng nhắc nhẹ",
  badDelta: "Ngưỡng nhắc rõ",
  degrees: "độ",
  ratio: "tỉ lệ",
  resetOverrides: "Trả về mặc định",
  data: "Dữ liệu",
} as const;

export const storageMessages = {
  unavailable: "Lịch sử sẽ không được lưu trên thiết bị này.",
  recoveryPrompt: "Phiên học trước bị ngắt. Lưu lại kết quả không?",
  deleteAllConfirm: "Xoá toàn bộ lịch sử phiên học trên thiết bị này?",
} as const;
